import { useState, useEffect } from 'react';
import { ImageIcon, AlertCircle, AlertTriangle } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';
import { Badge } from '@/components/ui/badge';

interface SafeImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackText?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
}

export const SafeImage = ({ src, alt, className = '', fallbackText, loading = 'lazy', fetchPriority }: SafeImageProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const { isAdmin } = useAdmin();

  // Détecter si l'alt text est manquant ou générique
  const hasValidAlt = alt && alt.trim() !== '' && !alt.match(/^(Image|Photo|Picture)\s*\d*$/i);
  const showAltWarning = isAdmin && !hasValidAlt;

  // Timeout de sécurité : forcer l'affichage après 3s (bug Chrome cache)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded && !imageError) {
        console.log(`[SafeImage] Force showing image after timeout: ${src}`);
        setForceShow(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [src, imageLoaded, imageError]);

  // Si pas de src ou erreur de chargement
  if (!src || imageError) {
    return (
      <div className={`${className} bg-muted flex flex-col items-center justify-center border border-border`}>
        <AlertCircle className="w-8 h-8 text-muted-foreground mb-2" />
        <span className="text-xs text-muted-foreground text-center px-2">
          {fallbackText || 'Image introuvable'}
        </span>
        <span className="text-xs text-muted-foreground/60 mt-1">
          Cliquez sur "Changer" pour uploader
        </span>
      </div>
    );
  }

  const showImage = imageLoaded || forceShow;

  // Helper to create responsive image URLs for Supabase Storage
  const createResponsiveSrc = (url: string) => {
    if (!url.includes('supabase.co/storage')) {
      return { src: url, srcSet: undefined, sizes: undefined };
    }
    
    const separator = url.includes('?') ? '&' : '?';
    return {
      src: `${url}${separator}width=800`,
      srcSet: `${url}${separator}width=400 400w, ${url}${separator}width=800 800w, ${url}${separator}width=1200 1200w`,
      sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
    };
  };

  const { src: responsiveSrc, srcSet, sizes } = createResponsiveSrc(src);

  return (
    <div className="relative w-full h-full">
      {!showImage && (
        <div className={`${className} bg-muted flex items-center justify-center animate-pulse`}>
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
        </div>
      )}
      <img
        src={responsiveSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={`${className} ${!showImage ? 'hidden' : ''}`}
        onError={() => {
          console.error(`[SafeImage] Failed to load: ${src}`);
          setImageError(true);
        }}
        onLoad={() => {
          console.log(`[SafeImage] Image loaded: ${src}`);
          setImageLoaded(true);
        }}
        loading={loading}
        fetchPriority={fetchPriority}
      />
      {showAltWarning && showImage && (
        <Badge 
          variant="destructive" 
          className="absolute top-2 left-2 z-10 bg-yellow-500 hover:bg-yellow-600 text-foreground flex items-center gap-1"
        >
          <AlertTriangle className="h-3 w-3" />
          <span className="text-xs">Alt text manquant</span>
        </Badge>
      )}
    </div>
  );
};
